/**
 * Street FURNITURE: lamp posts, trees, planters, signage and cones along the
 * footways, scaled from each prop's measured size to a real-world height, plus
 * the dusk lamp lighting. Placed in the kerbside furniture zone, never in the
 * carriageway or a doorway.
 */
import * as pc from 'playcanvas'
import { registerCollider } from '../colliders'
import { IS_DUSK } from './districtShared'
import { getCachedAsset, onAssetReady } from '../assets'
import { STREET_PROPS, streetPropScale, computeStreetProps } from './district'

export function buildStreetFurniture(parent: pc.Entity): void {
  for (const p of computeStreetProps()) {
    const def = STREET_PROPS[p.kind]
    if (!def) continue
    const s = streetPropScale(def)

    const holder = new pc.Entity(`street-${p.id}`)
    holder.setLocalScale(s, s, s)
    holder.setPosition(p.x, 0, p.z)
    holder.setEulerAngles(0, p.rotY, 0)
    parent.addChild(holder)

    const attach = (asset: pc.Asset): void => {
      if (!asset.resource) return
      const container = asset.resource as pc.ContainerResource
      // Furniture receives shadows but does not cast: at this count each
      // caster is redrawn once per shadow cascade for a shadow nobody reads.
      holder.addChild(container.instantiateRenderEntity({ castShadows: false }))
    }
    const cached = getCachedAsset(def.file)
    if (cached) attach(cached)
    else onAssetReady(def.file, attach)

    // Only the substantial props block movement; walking through a traffic
    // cone is less annoying than being stopped by one.
    if (p.kind === 'lamp' || p.kind === 'tree' || p.kind === 'planter') {
      const r = p.kind === 'planter' ? 0.6 : 0.25
      registerCollider({
        minX: p.x - r, maxX: p.x + r,
        minY: 0, maxY: def.targetHeight,
        minZ: p.z - r, maxZ: p.z + r,
      })
    }

    // At dusk the lamp posts light: an omni at the head casts the pool of warm
    // light onto the pavement, and a tiny emissive quad makes the source itself
    // glow so it reads as a lamp rather than a spotlight from nowhere. Gated on
    // the same flag as the windows, so by day there is no extra light at all.
    if (IS_DUSK && p.kind === 'lamp') {
      const headY = def.targetHeight - 0.4

      const light = new pc.Entity(`lamp-light-${p.id}`)
      light.addComponent('light', {
        type: 'omni',
        color: new pc.Color(1.0, 0.85, 0.55),
        intensity: 6,
        range: 14,
        castShadows: false,
      })
      light.setPosition(p.x, headY, p.z)
      parent.addChild(light)

      const bulbMat = new pc.StandardMaterial()
      bulbMat.emissive = new pc.Color(1.0, 0.88, 0.60)
      bulbMat.emissiveIntensity = 6
      bulbMat.useLighting = false
      bulbMat.update()
      const bulb = new pc.Entity(`lamp-bulb-${p.id}`)
      bulb.addComponent('render', { type: 'box' })
      if (bulb.render) {
        for (const mi of bulb.render.meshInstances) mi.material = bulbMat
        bulb.render.castShadows = false
      }
      bulb.setLocalScale(0.3, 0.3, 0.3)
      bulb.setPosition(p.x, headY, p.z)
      parent.addChild(bulb)
    }
  }
}

/** A door leaf that swings open when the player is close. */
